import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Copy, LoaderCircle, LogOut, Trash2, UserPlus } from 'lucide-react';
import { useCallback } from 'react';
import { toast } from 'sonner';

import { Avatar, AvatarFallback, AvatarImage } from '@/_shared/components/ui/avatar';
import type { WorkspaceRole } from '@/_shared/contexts/WorkspaceContext';
import { useWorkspace } from '@/_shared/contexts/WorkspaceContext';
import { useAuth } from '@/_shared/hooks/useAuth';
import { useOrpc } from '@/_shared/hooks/useOrpc';
import { usePageTitle } from '@/_shared/hooks/usePageTitle';

function roleBadgeClass(role: WorkspaceRole): string {
  if (role === 'owner') return 'bg-brand/10 text-brand';
  if (role === 'admin') return 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400';
  return 'bg-muted text-muted-foreground';
}

export function SettingsMembers(): React.ReactElement {
  usePageTitle('Members — Settings');
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { slug, isOwner, isAdmin } = useWorkspace();
  const orpc = useOrpc();

  const { data, isLoading } = useQuery(orpc.workspace.get.queryOptions({ input: {} }));

  const { data: invitesData } = useQuery({
    ...orpc.workspace.listInvites.queryOptions({ input: {} }),
    enabled: isAdmin,
  });

  const members = data?.members ?? [];
  const invites = (invitesData?.invites ?? []).filter((i) => !i.isRevoked);

  const createInviteMutation = useMutation(
    orpc.workspace.createInvite.mutationOptions({
      onSuccess: () => {
        toast.success('Invite link created');
        void queryClient.invalidateQueries({
          queryKey: orpc.workspace.listInvites.queryOptions({ input: {} }).queryKey,
        });
      },
      onError: () => toast.error('Failed to create invite'),
    }),
  );

  const revokeInviteMutation = useMutation(
    orpc.workspace.revokeInvite.mutationOptions({
      onSuccess: () => {
        toast.success('Invite revoked');
        void queryClient.invalidateQueries({
          queryKey: orpc.workspace.listInvites.queryOptions({ input: {} }).queryKey,
        });
      },
      onError: () => toast.error('Failed to revoke invite'),
    }),
  );

  const removeMemberMutation = useMutation(
    orpc.workspace.removeMember.mutationOptions({
      onSuccess: () => {
        toast.success('Member removed');
        void queryClient.invalidateQueries({
          queryKey: orpc.workspace.get.queryOptions({ input: {} }).queryKey,
        });
      },
      onError: () => toast.error('Failed to remove member'),
    }),
  );

  const updateRoleMutation = useMutation(
    orpc.workspace.updateMemberRole.mutationOptions({
      onSuccess: () => {
        toast.success('Role updated');
        void queryClient.invalidateQueries({
          queryKey: orpc.workspace.get.queryOptions({ input: {} }).queryKey,
        });
      },
      onError: (err) => toast.error(err.message || 'Failed to update role'),
    }),
  );

  const handleLeave = useCallback(async () => {
    if (!slug) return;
    try {
      const res = await fetch('/api/workspace/leave', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ workspaceId: slug }),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(body.error ?? 'Failed to leave workspace');
      }
      await queryClient.invalidateQueries();
      toast.success('Left workspace');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to leave workspace');
    }
  }, [queryClient, slug]);

  const copyInviteUrl = useCallback((inviteId: string) => {
    const url = `${window.location.origin}/invite/${inviteId}`;
    void navigator.clipboard.writeText(url);
    toast.success('Invite link copied');
  }, []);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16 text-muted-foreground">
        <LoaderCircle size={24} className="animate-spin" />
      </div>
    );
  }

  return (
    <>
      {/* Members list */}
      <section className="mb-8 rounded border border-border bg-card p-6">
        <h2 className="mb-4 text-sm font-bold tracking-wider text-muted-foreground uppercase">Members</h2>
        <div className="space-y-3">
          {members.map((member) => {
            const isSelf = member.userId === user?.id;
            const isMemberOwner = member.role === 'owner';
            // Admins are peers: any admin can manage (promote/demote/remove) another admin.
            // Only the workspace owner is protected.
            const canManage = isAdmin && !isSelf && !isMemberOwner;

            return (
              <div
                key={member.userId}
                className="flex items-center gap-3 rounded-md border border-border bg-background px-4 py-3"
              >
                <Avatar className="size-8">
                  <AvatarImage src={member.user.githubAvatarUrl ?? undefined} alt={member.user.githubLogin} />
                  <AvatarFallback>{member.user.githubLogin[0]?.toUpperCase()}</AvatarFallback>
                </Avatar>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-foreground">{member.user.githubLogin}</p>
                </div>

                {/* Role badge or role selector */}
                {canManage ? (
                  <select
                    aria-label={`Role for ${member.user.githubLogin}`}
                    value={member.role}
                    onChange={(e) =>
                      updateRoleMutation.mutate({
                        userId: member.userId,
                        role: e.target.value as 'admin' | 'member',
                      })
                    }
                    disabled={updateRoleMutation.isPending}
                    className="shrink-0 rounded border border-border bg-background px-2 py-0.5 text-[0.625rem] font-semibold tracking-wider text-muted-foreground uppercase transition-colors focus:border-brand focus:outline-none disabled:opacity-50"
                  >
                    <option value="admin">Admin</option>
                    <option value="member">Member</option>
                  </select>
                ) : (
                  <span
                    className={`shrink-0 rounded-full px-2 py-0.5 text-[0.625rem] font-semibold tracking-wider uppercase ${roleBadgeClass(member.role)}`}
                  >
                    {member.role}
                  </span>
                )}

                {canManage && (
                  <button
                    onClick={() => removeMemberMutation.mutate({ userId: member.userId })}
                    disabled={removeMemberMutation.isPending}
                    className="shrink-0 rounded border border-border p-1.5 text-muted-foreground transition-colors hover:border-red-500/50 hover:text-red-700"
                    title="Remove member"
                  >
                    <Trash2 size={12} />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </section>

      {/* Invite section (admin or owner) */}
      {isAdmin && (
        <section className="mb-8 rounded border border-border bg-card p-6">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-sm font-bold tracking-wider text-muted-foreground uppercase">Invite Links</h2>
            <button
              onClick={() => createInviteMutation.mutate({})}
              disabled={createInviteMutation.isPending}
              className="inline-flex items-center gap-2 rounded border border-brand bg-brand/10 px-3 py-1.5 text-xs text-brand transition-colors hover:bg-brand/20 disabled:opacity-30"
            >
              {createInviteMutation.isPending ? (
                <LoaderCircle size={12} className="animate-spin" />
              ) : (
                <UserPlus size={12} />
              )}
              Create Invite
            </button>
          </div>
          {invites.length === 0 ? (
            <p className="text-sm text-muted-foreground">No active invites</p>
          ) : (
            <div className="space-y-2">
              {invites.map((invite) => {
                const expired = new Date(invite.expiresAt) < new Date();
                return (
                  <div
                    key={invite.id}
                    className="flex items-center gap-3 rounded border border-border bg-background px-3 py-2"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-mono text-xs text-muted-foreground">{invite.id}</p>
                      <p className="text-[0.625rem] text-muted-foreground/60">
                        {invite.useCount} use{invite.useCount !== 1 ? 's' : ''}
                        {invite.maxUses !== null && ` / ${invite.maxUses} max`}
                        {' · '}
                        {expired ? 'Expired' : `Expires ${new Date(invite.expiresAt).toLocaleDateString()}`}
                      </p>
                    </div>
                    <button
                      onClick={() => copyInviteUrl(invite.id)}
                      className="shrink-0 rounded border border-border p-1.5 text-muted-foreground transition-colors hover:border-brand hover:text-brand"
                      title="Copy invite URL"
                    >
                      <Copy size={12} />
                    </button>
                    <button
                      onClick={() => revokeInviteMutation.mutate({ inviteId: invite.id })}
                      disabled={revokeInviteMutation.isPending}
                      className="shrink-0 rounded border border-border p-1.5 text-muted-foreground transition-colors hover:border-red-500/50 hover:text-red-700 disabled:opacity-30"
                      title="Revoke invite"
                    >
                      <Trash2 size={12} />
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </section>
      )}

      {/* Leave workspace (non-owners only) */}
      {!isOwner && (
        <section className="rounded border border-red-200/50 bg-red-50/50 p-6">
          <h2 className="mb-2 text-sm font-bold tracking-wider text-red-700/70 uppercase">Leave Workspace</h2>
          <p className="mb-4 text-sm text-muted-foreground">
            You will lose access to all shared dashboards, settings, and sources in this workspace.
          </p>
          <button
            onClick={() => void handleLeave()}
            className="inline-flex items-center gap-2 rounded border border-red-500/50 px-4 py-2 text-sm text-red-700 transition-colors hover:bg-red-100"
          >
            <LogOut size={14} />
            Leave Workspace
          </button>
        </section>
      )}
    </>
  );
}
